import type { OvpClient } from '@bldsoft/cappie/api/ovp'
import type { API } from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'
import { payload } from '../../result'
import { withTotal } from '../../utils/paginatedList'
import { ApiTag } from '../tags/index.h'
import { format, formatContentTag } from './format'
import type { ApiContent, ApiContentGenresToLanguages } from './index.h'

export function use(
  client: OvpClient,
  content: API['content'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // GET /api/web/content
  // Used at search page for content loading in grid
  content.base.getManyOldFx.use(
    async ({
      limit,
      offset,
      by = _defaults.sortBy,
      order = _defaults.sortOrder,
      q,
      tagIds,
      collectionIds,
      creatorId,
    }) => {
      const data = payload<{ results: ApiContent[] }>(
        await client.get.api.web.content({
          query: {
            limit,
            offset,
            sortBy: by,
            sortOrder: order,
            q,
            tagIds,
            collectionIds,
            creatorId,
          },
        })
      )

      if (!data) {
        throw new Error('Empty answer in content.base.getManyOldFx')
      }

      return (data.results || []).map(format)
    }
  )

  // GET /api/web/content/by-tag/{id} (by default), /api/web/content/by-collection/{id}
  content.base.getManyFx.use(
    async ({
      getByTag = true,
      id,
      limit,
      offset,
      by = _defaults.sortBy,
      order = _defaults.sortOrder,
      q,
      creatorId,
    }) => {
      const query = {
        limit,
        offset,
        sortBy: by,
        sortOrder: order,
        q,
        creatorId,
      }

      const result = getByTag
        ? await client.get.api.web.content.byTag(id, { query })
        : await client.get.api.web.content.byCollection(id, { query })

      const data = payload<{ results: ApiContent[]; total: number }>(result)

      if (!data) {
        throw new Error('Empty answer in content.base.getManyFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/content
  // GET /api/web/content/by-genre/{genreId}
  // GET /api/web/content/by-genre/{genreId}?languageId={languageIdQueryParam}
  // GET /api/web/content/by-collection/{collectionId}
  content.base.getContentFx.use(
    async ({ genreId, collectionId, limit, offset, languageIdQueryParam }) => {
      const query = {
        limit,
        offset,
        languageId: languageIdQueryParam,
      }

      let result
      if (genreId) {
        result = await client.get.api.web.content.byGenre(genreId, { query })
      } else if (collectionId) {
        result = await client.get.api.web.content.byCollection(collectionId, {
          query,
        })
      } else {
        result = await client.get.api.web.content({ query })
      }

      const data = payload<{ results: ApiContent[]; total: number }>(result)

      if (!data) {
        throw new Error('Empty answer in content.base.getContentByGenreFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/content/genres-to-languages
  content.base.getGenresToLanguagesFx.use(async () => {
    const data = payload<ApiContentGenresToLanguages>(
      await client.get.api.web.content.genresToLanguages()
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getGenresToLanguagesFx')
    }

    return data || []
  })

  // GET /api/web/content/${id}
  content.base.getOneFx.use(async ({ id, headers = {} }) => {
    const data = payload<ApiContent>(
      await client.get.api.web.content(id)({
        headers: Object.keys(headers).length ? headers : undefined,
      })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getOneFx')
    }

    return format(data)
  })

  // GET /api/web/content/recommended
  content.base.getRecommendedFx.use(
    async ({ limit, offset, by, order = _defaults.sortOrder, q }) => {
      const data = payload<{ results: ApiContent[]; total: number }>(
        await client.get.api.web.content.recommended({
          query: { limit, offset, sortBy: by, sortOrder: order, q },
        })
      )

      if (!data) {
        throw new Error('Empty answer in content.base.getRecommendedFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/content/my-list
  content.base.getFavoritesFx.use(async ({ limit, offset }) => {
    const data = payload<{ results: ApiContent[]; total: number }>(
      await client.get.api.web.content.myList({ query: { limit, offset } })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getFavoritesFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // POST /api/web/content/my-list/:id/add
  content.base.addToFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.content.myList(id).add())
  })

  // POST /api/web/content/my-list/:id/remove
  content.base.removeFromFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.content.myList(id).remove())
  })

  // GET /api/web/content/genres
  content.base.getContentGenresFx.use(async ({ withDescriptions }) => {
    const data = payload<{ results: ApiTag[]; total: number }>(
      await client.get.api.web.content.genres({
        query: { withDescriptions },
      })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getContentGenresFx')
    }

    const results = (data.results || []).map(formatContentTag)

    return withTotal(results, data.total)
  })

  // GET /api/web/content/languages
  content.base.getContentLanguagesFx.use(async ({ withDescriptions }) => {
    const data = payload<{ results: ApiTag[]; total: number }>(
      await client.get.api.web.content.languages({
        query: { withDescriptions },
      })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getContentLanguagesFx')
    }

    const results = (data.results || []).map(formatContentTag)

    return withTotal(results, data.total)
  })

  // GET /api/web/content-set/:id
  content.base.getOneContentSetFx.use(async ({ id, headers = {} }) => {
    const data = payload<ApiContent>(
      await client.get.api.web.contentSet(id, {
        headers: Object.keys(headers).length ? headers : undefined,
      })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getOneContentSetFx')
    }

    return format(data)
  })

  // GET /api/web/content-set/vod/{id}
  content.base.getContentSetVodFx.use(async ({ limit, offset, id }) => {
    const data = payload<{ results: ApiContent[]; total: number }>(
      await client.get.api.web.contentSet.vod(id, { query: { limit, offset } })
    )

    if (!data) {
      throw new Error('Empty answer in content.base.getContentSetVodFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })
}
